import { useState, useEffect } from 'react';
import { mapsAPI } from '../../services/api';
import { X, Save, Calendar, Globe, Lock, Loader2 } from 'lucide-react';
import './MapEditModal.css';

export default function MapEditModal({ map, onClose, onUpdate }) {
    const [formData, setFormData] = useState({
        title: '', description: '', startTime: '', endTime: '',
        isPublic: true, isActive: true
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!map) return;
        const fmt = (d) => {
            const dt = new Date(d);
            return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}T${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
        };
        setFormData({
            title: map.title || '',
            description: map.description || '',
            startTime: fmt(map.startTime),
            endTime: fmt(map.endTime),
            isPublic: map.isPublic !== undefined ? map.isPublic : true,
            isActive: map.isActive !== undefined ? map.isActive : true,
        });
    }, [map]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        const start = new Date(formData.startTime);
        const end = new Date(formData.endTime);
        if (end <= start) { setError('End time must be after start time'); return; }

        try {
            setLoading(true);
            await mapsAPI.updateMap(map.id, {
                title: formData.title,
                description: formData.description,
                startTime: start.toISOString(),
                endTime: end.toISOString(),
                isPublic: formData.isPublic,
                isActive: formData.isActive,
            });
            onUpdate();
            onClose();
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to update map');
        } finally {
            setLoading(false);
        }
    };

    if (!map) return null;

    return (
        <div className="mem-overlay" onClick={onClose}>
            <div className="mem-modal" onClick={e => e.stopPropagation()}>
                <div className="mem-header">
                    <h2 className="mem-title">Edit Map</h2>
                    <button className="mem-close-btn" onClick={onClose} disabled={loading}>
                        <X size={16} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="mem-body">
                    {error && <div className="mem-error">{error}</div>}

                    {/* Map ID (read-only) */}
                    <div className="mem-field">
                        <label className="mem-label">Map ID</label>
                        <input type="text" value={map.mapId} disabled className="mem-input mem-input--disabled" />
                    </div>

                    {/* Title */}
                    <div className="mem-field">
                        <label className="mem-label">Title <span className="mem-required">*</span></label>
                        <input type="text" name="title" value={formData.title} onChange={handleChange}
                            className="mem-input" required maxLength={100} placeholder="Enter map title" />
                        <p className="mem-char-count">{formData.title.length}/100</p>
                    </div>

                    {/* Description */}
                    <div className="mem-field">
                        <label className="mem-label">Description <span className="mem-optional">optional</span></label>
                        <textarea name="description" value={formData.description} onChange={handleChange}
                            className="mem-input mem-textarea" rows={3} maxLength={500} placeholder="Optional description" />
                        <p className="mem-char-count">{formData.description.length}/500</p>
                    </div>

                    {/* Dates */}
                    <div className="mem-field-row">
                        <div className="mem-field">
                            <label className="mem-label"><Calendar size={12} /> Start Time <span className="mem-required">*</span></label>
                            <input type="datetime-local" name="startTime" value={formData.startTime}
                                onChange={handleChange} className="mem-input" required />
                        </div>
                        <div className="mem-field">
                            <label className="mem-label"><Calendar size={12} /> End Time <span className="mem-required">*</span></label>
                            <input type="datetime-local" name="endTime" value={formData.endTime}
                                onChange={handleChange} className="mem-input" required />
                        </div>
                    </div>

                    {/* Visibility toggles */}
                    <div className="mem-toggles">
                        <label className="mem-toggle-row">
                            <input type="checkbox" name="isPublic" checked={formData.isPublic} onChange={handleChange} className="mem-checkbox" />
                            <span className="mem-toggle-icon">
                                {formData.isPublic ? <Globe size={14} className="mem-icon--blue" /> : <Lock size={14} className="mem-icon--gray" />}
                            </span>
                            <span className="mem-toggle-label">{formData.isPublic ? 'Public map' : 'Private map'}</span>
                            <span className="mem-toggle-sub">{formData.isPublic ? 'Anyone with the link can view' : 'Only you can view'}</span>
                        </label>
                        <label className="mem-toggle-row">
                            <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleChange} className="mem-checkbox" />
                            <span className="mem-toggle-icon" />
                            <span className="mem-toggle-label">{formData.isActive ? 'Active' : 'Inactive'}</span>
                            <span className="mem-toggle-sub">{formData.isActive ? 'Visible and accessible' : 'Hidden and not accessible'}</span>
                        </label>
                    </div>

                    {/* Map info */}
                    <div className="mem-info-box">
                        <p className="mem-info-heading">Map Information</p>
                        <div className="mem-info-grid">
                            <span className="mem-info-item"><strong>Created:</strong> {new Date(map.createdAt).toLocaleString()}</span>
                            <span className="mem-info-item"><strong>Updated:</strong> {new Date(map.updatedAt).toLocaleString()}</span>
                            <span className="mem-info-item"><strong>Visits:</strong> {map.visitCount || 0}</span>
                            <span className="mem-info-item"><strong>Markers:</strong> {map.markersCount || 0}</span>
                        </div>
                    </div>

                    {/* Share link */}
                    <div className="mem-field">
                        <label className="mem-label">Share Link</label>
                        <div className="mem-copy-row">
                            <input type="text" value={map.shareUrl} readOnly className="mem-input mem-input--disabled mem-input--mono" />
                            <button type="button" className="mem-copy-btn"
                                onClick={() => { navigator.clipboard.writeText(map.shareUrl); alert('Copied!'); }}>
                                Copy
                            </button>
                        </div>
                    </div>

                    {/* Footer actions */}
                    <div className="mem-footer">
                        <button type="button" className="adm-btn adm-btn--ghost" onClick={onClose} disabled={loading}>Cancel</button>
                        <button type="submit" className="adm-btn adm-btn--primary" disabled={loading}>
                            {loading
                                ? <><Loader2 size={14} className="aud-spin" /> Saving…</>
                                : <><Save size={14} /> Save Changes</>
                            }
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}